import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore/lite';
import slugify from 'slugify';
import { v4 as uuidv4 } from 'uuid';

import SectionData from '../types/SectionData';
import { firestore } from '../utils/firebase';

export type NewSectionData = Omit<SectionData, 'id' | 'slug'>;

const COLLECTION_NAME = 'sections';

export default class SectionApi {
  private static readonly collectionReference = collection(firestore, COLLECTION_NAME);

  public static async addSection(section: NewSectionData): Promise<string> {
    const docRef = doc(this.collectionReference);
    await setDoc(docRef, {
      ...section,
      slug: slugify(section.name) + uuidv4().trim().substring(0, 8),
      id: docRef.id,
    });
    return docRef.id;
  }

  public static async updateSection(section: SectionData): Promise<string> {
    const docRef = doc(this.collectionReference, section.id);
    await updateDoc(docRef, {
      ...section,
      slug: slugify(section.name) + uuidv4().trim().substring(0, 8),
      id: docRef.id,
    });
    return docRef.id;
  }

  /**
   * Fetches a single section by its id.
   * @param id
   */
  public static async getSectionById(id: string): Promise<SectionData> {
    const querySnaps = await getDocs(query(this.collectionReference, where('id', '==', id)));

    const docSnap = querySnaps.docs[0];
    return { ...docSnap.data(), id: docSnap.id } as SectionData;
  }

  /**
   * Fetches all sections belonging to module given by moduleId. Sections are sorted by order (ascending).
   * @param skillId
   */
  public static async getSectionsBySkillId(skillId: string): Promise<SectionData[]> {
    const querySnaps = await getDocs(
      query(this.collectionReference, where('skillId', '==', skillId), orderBy('order', 'asc'))
    );
    return querySnaps.docs.map(doc => ({ ...doc.data(), id: doc.id } as SectionData));
  }

  public static async getSectionBySlug(slug: string): Promise<SectionData> {
    const querySnaps = await getDocs(query(this.collectionReference, where('slug', '==', slug)));
    const doc = querySnaps.docs[0];
    return { ...doc.data(), id: doc.id } as SectionData;
  }

  /**
   * Update section by module id
   * @param moduleId
   * @param newSectionData
   * @returns
   */
  public static async updateSectionByModuleId(moduleId: string, newSectionData: SectionData) {
    return await updateDoc(doc(this.collectionReference, newSectionData.id), {
      ...newSectionData,
    });
  }

  public static async updateSections(sections: SectionData[]): Promise<void> {
    const batch = writeBatch(firestore);
    sections.forEach(section => {
      const docRef = doc(this.collectionReference, section.id);
      batch.update(docRef, { ...section });
    });
    return await batch.commit();
  }

  public static async removeSection(section: SectionData): Promise<void> {
    return await deleteDoc(doc(this.collectionReference, section.id));
  }
}
